import fonts from 'common/constants/fonts'

import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BulletListInterface } from 'common/types/entities/BulletListInterface'
import EntityInterface, {
  EntityUnionType,
} from 'common/types/entities/EntityInterface'
import { HeadlineInterface } from 'common/types/entities/HeadlineInterface'
import { TextInterface } from 'common/types/entities/TextInterface'
import ServerPage from 'server/types/ServerPageInterface'

type EntityWithFontFileIdType =
  | TextInterface
  | BulletListInterface
  | HeadlineInterface

const isRequiredEntities = (entity: EntityInterface) => {
  return (
    entity.type === EntityTypeEnum.Text ||
    entity.type === EntityTypeEnum.BulletList ||
    entity.type === EntityTypeEnum.Headline
  )
}

const isNonCustomFontFamily = (fontFamily?: string) =>
  fonts.safe.includes(fontFamily as string) ||
  fonts.google.includes(fontFamily ?? '') ||
  fontFamily === 'inherit' ||
  !fontFamily

const removeFontFileIdOrFontStyleAndWeight = (
  entity: EntityWithFontFileIdType,
): [EntityWithFontFileIdType, boolean] => {
  const isFontNonCustom = isNonCustomFontFamily(entity.fontFamily)
  const isMobileFontNonCustom = isNonCustomFontFamily(entity.mobileFontFamily)
  const shouldRemoveFontFileId = isFontNonCustom && Boolean(entity.fontFileId)

  const shouldRemoveMobileFontFileId =
    isMobileFontNonCustom && Boolean(entity.mobileFontFileId)

  const shouldRemoveFontStyleAndWeight =
    entity.fontFamily === 'inherit' || !isFontNonCustom
  const shouldRemoveMobileFontStyleAndWeight =
    entity.fontFamily === 'inherit' || !isMobileFontNonCustom

  if (
    !shouldRemoveFontFileId &&
    !shouldRemoveMobileFontFileId &&
    !shouldRemoveFontStyleAndWeight &&
    !shouldRemoveMobileFontStyleAndWeight
  ) {
    return [entity, false]
  }

  const {
    fontFileId,
    mobileFontFileId,
    fontStyle,
    mobileFontStyle,
    fontWeight,
    mobileFontWeight,
    ...rest
  } = entity

  const updatedEntity: EntityWithFontFileIdType = {
    ...rest,
    ...(shouldRemoveFontFileId ? {} : { fontFileId }),
    ...(shouldRemoveMobileFontFileId ? {} : { mobileFontFileId }),
    ...(shouldRemoveFontStyleAndWeight ? {} : { fontStyle, fontWeight }),
    ...(shouldRemoveMobileFontStyleAndWeight
      ? {}
      : { mobileFontStyle, mobileFontWeight }),
  }

  const hasChanged =
    updatedEntity.fontFileId !== entity.fontFileId ||
    updatedEntity.mobileFontFileId !== entity.mobileFontFileId ||
    updatedEntity.fontStyle !== entity.fontStyle ||
    updatedEntity.mobileFontStyle !== entity.mobileFontStyle ||
    updatedEntity.fontWeight !== entity.fontWeight ||
    updatedEntity.mobileFontWeight !== entity.mobileFontWeight

  return [updatedEntity, hasChanged]
}

export const removeUnnecessaryFontProperties = (
  page: ServerPage,
): [ServerPage, boolean] => {
  let hasChanged = false
  const newEntities: Record<string, EntityUnionType> = {}

  // biome-ignore lint/complexity/noForEach: <explanation>
  Object.values(page.content.entities).forEach(entity => {
    if (isRequiredEntities(entity as EntityInterface)) {
      const [changedEntity, isModified] = removeFontFileIdOrFontStyleAndWeight(
        entity as EntityWithFontFileIdType,
      )
      newEntities[entity.id] = changedEntity
      if (isModified) {
        hasChanged = isModified
      }
    } else {
      newEntities[entity.id] = entity
    }
  })

  page.content.entities = newEntities

  return [page, hasChanged]
}
